<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Migration wrapper for the many q-badge usages in the app that were used as data
 * annotations rather than M3 notification badges — counts, durations, statuses.
 *
 * Lives in /migration rather than /components because it wraps q-badge directly,
 * which violates the rule that only primitives may reference Quasar components.
 * HrDataLabel is not the same concept as HrBadge (which is a strict M3 notification
 * badge with error color and always-floating behavior); the two happen to share the
 * same underlying Quasar element but serve different semantic purposes, which is
 * exactly why neither fits cleanly as a variant of the other.
 *
 * @status internal
 * @group lists-tables
 * @see https://quasar.dev/vue-components/badge Quasar q-badge API
 */
defineOptions({ name: 'HrDataLabel', inheritAttrs: false });

const props = defineProps({
  /** Short text content. Omit when using the default slot for complex content. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Positions the label absolutely at the top-end corner of the nearest relative parent.
   * The parent element must have `position: relative`.
   */
  floating: {
    type: Boolean,
    default: false,
  },
  /** Adds cursor-pointer and a hover state for interactive labels. */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    floating: props.floating,
    ...(props.label !== null && { label: props.label }),
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-badge
    v-ripple="hasTheme && clickable"
    v-bind="effectiveProps"
    :class="hasTheme ? ['hr-data-label', clickable && 'hr-data-label--clickable'] : undefined"
  >
    <!-- @slot default Custom label content; use instead of the `label` prop for rich content. -->
    <slot />
  </q-badge>
</template>

<style lang="scss" scoped>
.hr-data-label {
  background: var(--app-surface-variant);
  color: var(--app-on-surface-variant);
  border-radius: var(--app-shape-corner-full);
  font-size: var(--app-type-label-small-size);
  font-weight: var(--app-type-label-small-weight);
  line-height: var(--app-type-label-small-line-height);
  min-height: 16px;
  min-width: 16px;
  padding: 0 6px;
  gap: 4px;
}

.hr-data-label--clickable {
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--app-on-surface-variant) 8%, var(--app-surface-variant));
  }
}
</style>
